import React, { useCallback, useMemo } from 'react'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import LoadingSpinner from '../../components/loading/loading_spinner'
import { useOwnMatching } from '../../hooks/use_own_matching'
import EnterpriseCard from '../../components/cards/enterprise_card'
import RadioButtonGroup from '../../components/buttons/radio_button_group'
import { EnterpriseSummary } from '../../clients/kyousou_api_client'
import { useEnterpriseDetailModal } from './hooks/use_enterprise_detail_modal'
import { useInfiniteScroll } from '../../hooks/use_infinite_scroll'
import type { RadioButtonType } from '../../components/buttons/radio_button_group'
import {
  EnterpriseSortType,
  EnterpriseSortTypeLabel,
  EnterpriseSortTypeValue,
} from '../../types/enterprise'
import Breadcrumb from '../../components/common/breadcrumb'

// マッチング中の企業の型定義
type MatchingEnterprise = EnterpriseSummary & {
  matchingFrom: 'idea' | 'partner' | 'enterprise'
}

const MatchingEnterprisesPage: React.FC = () => {
  const {
    isLoading,
    isLoadingMore,
    hasMore,
    enterprises,
    loadMore,
    changeSort,
    currentSort,
  } = useOwnMatching()

  const { LoadingElement } = useInfiniteScroll(loadMore, isLoadingMore, hasMore)

  const handleSortChange = useCallback(
    (type: EnterpriseSortTypeValue) => {
      if (type !== currentSort) {
        changeSort(type)
      }
    },
    [currentSort, changeSort]
  )

  const sortButtons: RadioButtonType[] = useMemo(
    () => [
      // TODO: マッチング率ソートはパフォーマンス改善後に有効化
      // {
      //   label: EnterpriseSortTypeLabel[EnterpriseSortType.MATCHING_RATE],
      //   onClick: () => handleSortChange(EnterpriseSortType.MATCHING_RATE),
      // },
      {
        label: EnterpriseSortTypeLabel[EnterpriseSortType.CREATED_AT],
        onClick: () => handleSortChange(EnterpriseSortType.CREATED_AT),
      },
      {
        label: EnterpriseSortTypeLabel[EnterpriseSortType.NAME],
        onClick: () => handleSortChange(EnterpriseSortType.NAME),
      },
    ],
    [handleSortChange]
  )
  const { EnterpriseDetailModal, showEnterpriseDetail } =
    useEnterpriseDetailModal()

  // モーダル関連のハンドラー
  const handleCardClick = useCallback(
    (enterprise: MatchingEnterprise) => {
      showEnterpriseDetail(enterprise)
    },
    [showEnterpriseDetail]
  )

  return (
    <AuthenticatedLayout>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: 'ホーム', path: '/dashboard' },
            { label: 'マッチング企業' },
          ]}
        />

        {/* ヘッダー部分：タイトルとソートボタン */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">マッチング企業一覧</h1>
          <RadioButtonGroup buttons={sortButtons} />
        </div>
        {/* レンダリング部分 */}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {isLoading && enterprises.length === 0 ? (
            <div className="flex justify-center py-20 col-span-full">
              <LoadingSpinner />
            </div>
          ) : enterprises.length === 0 ? (
            <div className="col-span-full">
              <div className="text-center py-8 text-main-darkgray">
                マッチング企業がありません。
              </div>
            </div>
          ) : (
            <>
              {enterprises.map((ent, index) => (
                <div
                  key={`${ent.id}-${index}`}
                  onClick={() => handleCardClick(ent)}
                  className="cursor-pointer hover:opacity-80 transition-opacity"
                >
                  <EnterpriseCard
                    enterprise={ent}
                    initialized={!isLoading}
                    disableNavigation={true}
                  />
                </div>
              ))}
            </>
          )}

          {enterprises.length > 0 && hasMore && LoadingElement}
        </div>

        {/* 企業詳細モーダル */}
        <EnterpriseDetailModal />
      </div>
    </AuthenticatedLayout>
  )
}

export default MatchingEnterprisesPage
